import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Menu, Bell, Eye, Settings } from 'lucide-react';
import { TrialPill } from '@/components/billing/TrialPill';
import { NotificationDropdown } from '@/components/NotificationDropdown';
import { AccessibilityQuickPanel } from '@/components/accessibility/AccessibilityQuickPanel';
import { useTranslation } from '@/hooks/useTranslation';
import { useUnreadCount } from '@/hooks/useNotifications';

interface HeaderProps {
  onMobileMenuOpen: () => void;
  onSearchOpen: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onMobileMenuOpen, onSearchOpen }) => {
  const { t } = useTranslation();
  const { data: unreadCount } = useUnreadCount();
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false);
  const [isAccessibilityPanelOpen, setIsAccessibilityPanelOpen] = useState(false);
  const notificationDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target as Node)) {
        setIsNotificationDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header
      className="h-16 flex items-center justify-between p-3 md:px-6 border-b"
      style={{ backgroundColor: 'var(--color-bg-primary)', borderColor: 'var(--color-border-primary)' }}
    >
      {/* Mobile menu button */}
      <button
        onClick={onMobileMenuOpen}
        className="lg:hidden p-2 rounded-lg"
        style={{ color: 'var(--color-text-primary)' }}
      >
        <Menu className="w-6 h-6" />
      </button>

      {/* Search and Actions — pushed to the inline-end side via
          ms-auto so the cluster lands top-right in LTR and top-left in
          RTL. The previous `ms-auto me-auto ms-0` margin chain was a
          tangle of conflicting auto values that ended up centering. */}
      <div className="flex items-center gap-3 ms-auto">
        {/* Search trigger.
            Mobile / tablet (< lg): rendered as a round 44px icon button
            so it visually matches the Accessibility + Notification
            buttons next to it (was a rectangle before — looked like
            three buttons that didn't belong to the same set).
            Desktop (≥ lg): expands into the familiar search-bar pill
            with label + ⌘K hint. */}
        <button
          onClick={onSearchOpen}
          aria-label={t('common.search')}
          className="
            inline-flex items-center justify-center transition-all border
            min-h-11 min-w-11 rounded-full
            lg:rounded-lg lg:px-5 lg:min-h-[40px] lg:min-w-[300px] lg:justify-start lg:gap-3
          "
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            color: 'var(--color-text-tertiary)',
            borderColor: 'var(--color-border-primary)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--color-border-secondary)';
            e.currentTarget.style.color = 'var(--color-text-secondary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--color-border-primary)';
            e.currentTarget.style.color = 'var(--color-text-tertiary)';
          }}
        >
          <Search className="w-4 h-4" />
          <span className="hidden lg:inline text-sm">{t('common.search')}</span>
          <kbd
            className="hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded ms-auto font-semibold"
            style={{
              backgroundColor: 'var(--color-bg-primary)',
              color: 'var(--color-text-tertiary)',
              border: '1px solid var(--color-border-primary)',
            }}
          >
            {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}K
          </kbd>
        </button>

        {/* Accessibility / appearance quick panel — exposes language,
            light/dark mode, text scale, RTL override, contrast, and
            reduced motion from anywhere in the app. The full settings
            still live at /settings#accessibility (linked from inside
            the panel). */}
        <button
          type="button"
          onClick={() => setIsAccessibilityPanelOpen(true)}
          aria-label={t('accessibilityQuick.title')}
          className="p-2.5 rounded-full transition-colors border min-h-11 min-w-11 inline-flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{
            color: 'var(--color-text-secondary)',
            borderColor: 'var(--color-border-primary)',
            backgroundColor: 'var(--color-bg-secondary)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--color-border-secondary)';
            e.currentTarget.style.color = 'var(--color-text-primary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--color-border-primary)';
            e.currentTarget.style.color = 'var(--color-text-secondary)';
          }}
        >
          <Eye className="w-4 h-4" />
        </button>

        {/* Trial state + subscribe CTA. Self-hides on a paid plan. */}
        <TrialPill />

        {/* Settings — full app/clinic settings (voice language, signing PIN,
            profile, documents, privacy). Previously only reachable two clicks
            deep inside the sidebar's user dropdown; surfaced here so it's one
            click from any page, matching the other header affordances. */}
        <Link
          to="/settings"
          aria-label={t('nav.settings')}
          title={t('nav.settings')}
          className="p-2.5 rounded-full transition-colors border min-h-11 min-w-11 inline-flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{
            color: 'var(--color-text-secondary)',
            borderColor: 'var(--color-border-primary)',
            backgroundColor: 'var(--color-bg-secondary)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--color-border-secondary)';
            e.currentTarget.style.color = 'var(--color-text-primary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--color-border-primary)';
            e.currentTarget.style.color = 'var(--color-text-secondary)';
          }}
        >
          <Settings className="w-4 h-4" />
        </Link>

        <div className="relative" ref={notificationDropdownRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsNotificationDropdownOpen(!isNotificationDropdownOpen);
            }}
            aria-label="Notifications"
            className="p-2.5 rounded-full transition-colors relative border min-h-11 min-w-11 inline-flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{
              color: 'var(--color-text-secondary)',
              borderColor: 'var(--color-border-primary)',
              backgroundColor: 'var(--color-bg-secondary)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-border-secondary)';
              e.currentTarget.style.color = 'var(--color-text-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-border-primary)';
              e.currentTarget.style.color = 'var(--color-text-secondary)';
            }}
          >
            <Bell className="w-4 h-4" />
            {unreadCount && unreadCount > 0 ? (
              <span
                className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full ring-2"
                style={{
                  backgroundColor: 'var(--color-error)',
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  ['--tw-ring-color' as any]: 'var(--color-bg-secondary)',
                }}
              />
            ) : null}
          </button>
          <NotificationDropdown isOpen={isNotificationDropdownOpen} onClose={() => setIsNotificationDropdownOpen(false)} />
        </div>
      </div>

      <AccessibilityQuickPanel
        open={isAccessibilityPanelOpen}
        onOpenChange={setIsAccessibilityPanelOpen}
      />
    </header>
  );
};
